import { useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "../api";
import { IconClose, IconCheck } from "../icons";

/**
 * IMPORTAR A PLANILHA — em dois tempos: primeiro a prévia, depois a gravação.
 *
 * POR QUE A PRÉVIA EXISTE
 * -----------------------
 * A planilha que o laboratório mantinha à mão tem anos de edição: coluna
 * renomeada, linha em branco no meio, código repetido. Mandar o arquivo e ver
 * "gravado" não diz nada — quem importa precisa saber ANTES quantos
 * equipamentos entram novos, quantos vão ser atualizados e o que o sistema não
 * entendeu. É a diferença entre uma importação e um estrago.
 *
 * O servidor faz o trabalho duas vezes (uma para a prévia, outra para gravar) e
 * isso é de propósito: guardar o resultado da prévia em algum lugar do servidor
 * criaria um estado que envelhece — e o arquivo é pequeno.
 *
 * A IMPORTAÇÃO NUNCA APAGA: quem está no sistema e não veio na planilha
 * continua lá, e coluna que a planilha não trouxe não sobrescreve o que foi
 * digitado na tela.
 */

interface Previa {
  criar?: number;
  atualizar?: number;
  ignoradas?: string[];
  repetidas?: string[];
  colunasReconhecidas?: string[];
  colunasIgnoradas?: string[];
  amostra?: { codigo: string; acao: string; descricao: string | null }[];
}

export default function ImportarPlanilhaModal({
  recurso,
  titulo,
  onClose,
  onImportado,
}: {
  /** Prefixo das rotas: /api/<recurso>/planilha/... */
  recurso: string;
  /** Nome da aba, para o cabeçalho ("Análise de EMMS"). */
  titulo: string;
  onClose: () => void;
  /** Avisa a listagem para recarregar, com a mensagem do que entrou. */
  onImportado: (mensagem: string) => void;
}) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function escolher(f: File | null) {
    setErro(null);
    setPrevia(null);
    setArquivo(f);
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      setErro("O arquivo precisa ser .xlsx (Excel). Se a sua planilha é .xls ou .csv, abra no Excel e salve como .xlsx.");
      setArquivo(null);
      return;
    }
    setOcupado(true);
    try {
      setPrevia(await api.planilha.importar(recurso, f, false));
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui ler a planilha.");
      setArquivo(null);
    } finally {
      setOcupado(false);
    }
  }

  async function gravar() {
    if (!arquivo) return;
    setOcupado(true);
    setErro(null);
    try {
      const r = await api.planilha.importar(recurso, arquivo, true);
      onImportado(r.mensagem ?? "Planilha importada.");
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : "Não consegui importar a planilha.");
    } finally {
      setOcupado(false);
    }
  }

  const entram = (previa?.criar ?? 0) + (previa?.atualizar ?? 0);

  return createPortal(
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Importar planilha</h3>
            <p className="modal-sub">{titulo}</p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="modal-notice">
            <span className="modal-notice-ic">💡</span>
            <span>
              A importação <strong>não apaga nada</strong>. O cruzamento é pelo{" "}
              <strong>Código</strong>: quem já existe é atualizado, quem não existe é criado, e
              equipamento que está no sistema mas não veio na planilha continua como está.
            </span>
          </div>

          <label className="field field-wide">
            <span>Arquivo .xlsx</span>
            <input
              type="file"
              accept=".xlsx"
              onChange={(e) => escolher(e.target.files?.[0] ?? null)}
              disabled={ocupado}
            />
          </label>

          <p className="modal-sub">
            Não tem a planilha no formato certo?{" "}
            <button
              type="button"
              className="link-btn"
              onClick={() => api.planilha.baixar(recurso, "modelo").catch(() => {})}
            >
              Baixe o modelo em branco
            </button>{" "}
            — ele traz os nomes de coluna que o sistema reconhece e uma linha de exemplo.
          </p>

          {ocupado && !previa && <div className="empty">Lendo a planilha…</div>}

          {previa && (
            <div className="crud-previa">
              <h4>O que vai acontecer</h4>
              <ul>
                <li>
                  <strong>{previa.criar ?? 0}</strong> equipamento(s) novo(s)
                </li>
                <li>
                  <strong>{previa.atualizar ?? 0}</strong> já cadastrado(s), que serão atualizados
                </li>
                {!!previa.ignoradas?.length && (
                  <li>
                    <strong>{previa.ignoradas.length}</strong> linha(s) sem código — serão puladas
                  </li>
                )}
                {!!previa.repetidas?.length && (
                  <li>
                    Código repetido na planilha (só o primeiro entra):{" "}
                    <strong>{previa.repetidas.join(", ")}</strong>
                  </li>
                )}
              </ul>

              {!!previa.colunasIgnoradas?.length && (
                <p className="modal-sub">
                  Colunas que não reconheci e vou ignorar:{" "}
                  <strong>{previa.colunasIgnoradas.join(", ")}</strong>. Se alguma delas devia
                  entrar, me avise o nome certo.
                </p>
              )}

              {!!previa.amostra?.length && (
                <>
                  <h4>Amostra do que vem</h4>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descrição</th>
                        <th>O que será feito</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previa.amostra.map((a) => (
                        <tr key={a.codigo}>
                          <td>{a.codigo}</td>
                          <td>{a.descricao ?? "—"}</td>
                          <td>{a.acao === "criar" ? "cadastrar" : "atualizar"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              {entram === 0 && (
                <div className="form-error">
                  Nenhuma linha aproveitável. Confira se a primeira linha da planilha é o cabeçalho
                  e se existe uma coluna “Código”.
                </div>
              )}
            </div>
          )}

          {erro && <div className="form-error">{erro}</div>}
        </div>

        <div className="modal-foot">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={ocupado}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={gravar}
            disabled={ocupado || !previa || entram === 0}
          >
            <IconCheck size={17} /> {ocupado ? "Importando…" : `Importar ${entram} equipamento(s)`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
